import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import Toast, { ToastProps } from "../utils/Toast";

interface ToastContextType {
  showToast: (
    message: string,
    type?: ToastProps["type"],
    duration?: number | null // null means it won't auto-hide and will require manual closing the toast
  ) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType>({
  showToast: () => {},
  hideToast: () => {},
});

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    type: ToastProps["type"];
    duration: number | null;
  }>({
    open: false,
    message: "",
    type: "info",
    duration: 4000,
  });

  const showToast = useCallback((
    message: string,
    type: ToastProps["type"] = "info",
    duration: number | null = 4000
  ) => {
    setToast({
      open: true,
      message,
      type,
      duration,
    });
  }, []);

  const hideToast = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <Toast
        open={toast.open}
        message={toast.message}
        type={toast.type}
        duration={toast.duration}
        onClose={hideToast}
      />
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);

